import { useParams, Navigate, Link } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { ArrowLeft, Clock, Tag, Calendar, MessageCircle } from "lucide-react";
import { getT } from "@/lib/translations";
import { blogContent, getPost, slugMap } from "@/lib/blogContent";
import { waLink, PRESET } from "@/lib/whatsapp";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { FloatingWhatsApp, MobileStickyCTA } from "@/components/FloatingCTA";

const BlogSEO = ({ post, lang }) => {
  useEffect(() => {
    const origin = "https://benditotattoo.com";
    const pageUrl = `${origin}/${lang}/blog/${post.slug}`;
    const ogImage = `${origin}/images/logo/logo.jpeg`;
    const ogTitle = `${post.title} | Bendito Tattoo`;

    document.title = ogTitle;
    document.documentElement.setAttribute("lang", lang === "es" ? "es" : "en");

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = selector.replace("meta[", "").replace("]", "").split("=");
        el.setAttribute(k, v.replace(/"/g, ""));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    const setLink = (rel, href, hreflang) => {
      const sel = hreflang
        ? `link[rel="${rel}"][hreflang="${hreflang}"]`
        : `link[rel="${rel}"]`;
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (hreflang) el.setAttribute("hreflang", hreflang);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    setMeta('meta[name="description"]', "content", post.metaDescription);
    setMeta('meta[property="og:title"]', "content", ogTitle);
    setMeta('meta[property="og:description"]', "content", post.metaDescription);
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[property="og:url"]', "content", pageUrl);
    setMeta('meta[property="og:image"]', "content", ogImage);
    setMeta('meta[property="og:site_name"]', "content", "Bendito Tattoo");
    setMeta('meta[property="og:locale"]', "content", lang === "es" ? "es_MX" : "en_US");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", ogTitle);
    setMeta('meta[name="twitter:description"]', "content", post.metaDescription);
    setMeta('meta[name="twitter:image"]', "content", ogImage);

    // Canonical
    setLink("canonical", pageUrl);

    // Hreflang alternates for blog posts
    const otherLang = lang === "en" ? "es" : "en";
    const otherSlug = slugMap[otherLang]?.[post.id];
    if (otherSlug) {
      setLink("alternate", `${origin}/${lang}/blog/${post.slug}`, lang);
      setLink("alternate", `${origin}/${otherLang}/blog/${otherSlug}`, otherLang);
      setLink("alternate", `${origin}/en/blog/${lang === "en" ? post.slug : otherSlug}`, "x-default");
    }

    // Article JSON-LD
    const ldId = "ld-blogpost";
    let ld = document.getElementById(ldId);
    if (!ld) {
      ld = document.createElement("script");
      ld.id = ldId;
      ld.type = "application/ld+json";
      document.head.appendChild(ld);
    }
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.metaDescription,
      datePublished: post.date,
      dateModified: post.date,
      image: ogImage,
      inLanguage: lang === "es" ? "es" : "en",
      author: {
        "@type": "Organization",
        name: "Bendito Tattoo",
        url: origin,
      },
      publisher: {
        "@type": "Organization",
        name: "Bendito Tattoo",
        url: origin,
        logo: {
          "@type": "ImageObject",
          url: ogImage,
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": pageUrl,
      },
    });

    // Breadcrumb JSON-LD
    const bcId = "ld-breadcrumb";
    let bc = document.getElementById(bcId);
    if (!bc) {
      bc = document.createElement("script");
      bc.id = bcId;
      bc.type = "application/ld+json";
      document.head.appendChild(bc);
    }
    const blogLabel = lang === "es" ? "Blog" : "Blog";
    const homeLabel = lang === "es" ? "Inicio" : "Home";
    bc.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: homeLabel,
          item: `${origin}/${lang}`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: blogLabel,
          item: `${origin}/${lang}#blog`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: post.title,
          item: pageUrl,
        },
      ],
    });

    return () => {
      const ldEl = document.getElementById(ldId);
      if (ldEl) ldEl.remove();
      const bcEl = document.getElementById(bcId);
      if (bcEl) bcEl.remove();
    };
  }, [post, lang]);

  return null;
};

const renderBody = (body) => {
  const paragraphs = body.trim().split("\n\n");
  return paragraphs.map((p, i) => {
    const parts = p.split(/(\*\*.*?\*\*)/g);
    const rendered = parts.map((part, j) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={j} className="text-bendito-ink">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
    return (
      <p key={i} className="text-bendito-muted leading-relaxed mb-4">
        {rendered}
      </p>
    );
  });
};

export const BlogPost = () => {
  const { lang: langParam, slug } = useParams();
  const lang = langParam === "es" ? "es" : "en";

  // Compute post and redirect target before any hooks
  const { post, redirectTo } = useMemo(() => {
    if (!langParam || (langParam !== "en" && langParam !== "es")) {
      return { post: null, redirectTo: "/en" };
    }

    const found = getPost(lang, slug);
    if (found) return { post: found, redirectTo: null };

    // Try the other language and redirect to the equivalent slug
    const otherLang = lang === "en" ? "es" : "en";
    const otherPost = getPost(otherLang, slug);
    if (otherPost) {
      const currentSlug = slugMap[lang]?.[otherPost.id];
      if (currentSlug) {
        return { post: null, redirectTo: `/${lang}/blog/${currentSlug}` };
      }
    }
    return { post: null, redirectTo: `/${lang}` };
  }, [langParam, lang, slug]);

  // Get other posts for "More Articles" section
  const otherPosts = useMemo(() => {
    const allPosts = blogContent[lang] || blogContent.en;
    return Object.values(allPosts).filter((p) => p.slug !== post?.slug);
  }, [lang, post]);

  const t = getT(lang);
  const labels = lang === "es"
    ? { back: "Volver al blog", more: "Más Artículos" }
    : { back: "Back to blog", more: "More Articles" };
  const waMsg = lang === "es" ? PRESET.generalEs : PRESET.generalEn;

  // Scroll to top on mount / slug change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Handle redirects after all hooks
  if (redirectTo) return <Navigate to={redirectTo} replace />;

  return (
    <main className="bg-bendito-bg min-h-screen text-bendito-ink">
      <BlogSEO post={post} lang={lang} />
      <Header t={t} lang={lang} />

      <article className="pt-28 pb-16 md:pt-36 md:pb-24">
        <div className="max-w-3xl mx-auto px-5 md:px-8">
          {/* Back link */}
          <Link
            to={`/${lang}#blog`}
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-bendito-muted hover:text-bendito-orange transition-colors mb-8"
          >
            <ArrowLeft size={14} />
            {labels.back}
          </Link>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.3em] uppercase text-bendito-orange">
              <Tag size={12} />
              {post.category}
            </span>
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-bendito-muted">
              <Clock size={12} />
              {post.readTime}
            </span>
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-bendito-muted">
              <Calendar size={12} />
              {post.date}
            </span>
          </div>

          {/* Title */}
          <h1 className="font-heading text-bendito-ink text-3xl md:text-4xl lg:text-5xl uppercase tracking-tight leading-tight mb-10">
            {post.title}
          </h1>

          {/* Sections */}
          <div className="space-y-10">
            {post.sections.map((section, i) => (
              <section key={i}>
                <h2 className="font-heading text-bendito-ink text-xl md:text-2xl uppercase tracking-wide mb-4">
                  {section.heading}
                </h2>
                {renderBody(section.body)}
              </section>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-14 p-6 md:p-8 border border-bendito-orange/30 bg-bendito-orange/5">
            <p className="text-bendito-ink leading-relaxed mb-5">
              {post.cta}
            </p>
            <a
              href={waLink(waMsg)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-bendito-orange hover:bg-bendito-orangeDark text-black font-heading tracking-[0.2em] text-sm px-6 py-3 transition-colors"
            >
              <MessageCircle size={16} />
              {lang === "es" ? "Escribenos por WhatsApp" : "Message Us on WhatsApp"}
            </a>
          </div>

          {/* More Articles */}
          {otherPosts.length > 0 && (
            <div className="mt-16 pt-12 border-t border-bendito-ink/10">
              <h3 className="font-heading text-bendito-ink text-xl md:text-2xl uppercase tracking-wide mb-8">
                {labels.more}
              </h3>
              <div className="grid gap-6 sm:grid-cols-2">
                {otherPosts.map((op) => (
                  <Link
                    key={op.slug}
                    to={`/${lang}/blog/${op.slug}`}
                    className="group block border border-bendito-ink/10 hover:border-bendito-orange/40 bg-bendito-bg transition-colors p-5 md:p-6"
                  >
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.3em] uppercase text-bendito-orange mb-3">
                      <Tag size={11} />
                      {op.category}
                    </span>
                    <h4 className="font-heading text-bendito-ink text-base md:text-lg uppercase tracking-tight leading-snug mb-3 group-hover:text-bendito-orange transition-colors">
                      {op.title}
                    </h4>
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-bendito-muted">
                      <Clock size={11} />
                      {op.readTime}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Back link bottom */}
          <div className="mt-10">
            <Link
              to={`/${lang}#blog`}
              className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-bendito-muted hover:text-bendito-orange transition-colors"
            >
              <ArrowLeft size={14} />
              {labels.back}
            </Link>
          </div>
        </div>
      </article>

      <Footer t={t} lang={lang} />
      <FloatingWhatsApp lang={lang} />
      <MobileStickyCTA t={t} lang={lang} />
      <div className="h-16 md:hidden" />
    </main>
  );
};

export default BlogPost;
